<script setup lang="ts">
import { computed } from 'vue'
import { Button } from '@/components/shadcn'

type StyleType = 'default' | 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'sm' | 'lg'

const props = withDefaults(
  defineProps<{
    /** 링크 URL */
    href?: string
    /** 링크 텍스트 */
    text?: string
    /** 새 탭에서 열기 */
    target?: '_blank' | '_self' | '_parent' | '_top'
    /** 링크 스타일 테마 */
    styletype?: StyleType
    /** 비활성화 상태 */
    disabled?: boolean
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    styletype: 'default',
    disabled: false,
  },
)

const STYLE_PRESETS: Record<StyleType, { variant?: string; size?: string; class?: string }> = {
  default: { variant: 'link', class: 'text-primary underline-offset-4 hover:underline' },
  primary: { variant: 'link', class: 'text-primary-foreground bg-primary hover:bg-primary/90' },
  secondary: { variant: 'link', class: 'text-secondary-foreground bg-secondary hover:bg-secondary/80' },
  destructive: { variant: 'link', class: 'text-destructive hover:text-destructive/90' },
  outline: { variant: 'outline', class: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground' },
  ghost: { variant: 'ghost', class: 'hover:bg-accent hover:text-accent-foreground' },
  sm: { variant: 'link', size: 'sm', class: 'h-8 px-3 text-xs' },
  lg: { variant: 'link', size: 'lg', class: 'h-12 px-8 text-base' },
}

const mapped = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  const finalClass = [preset?.class, props.class].filter(Boolean).join(' ')

  return {
    variant: (preset?.variant || 'link') as 'link' | 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost',
    size: (preset?.size || 'sm') as 'xs' | 'sm' | 'md' | 'lg' | 'icon',
    class: finalClass,
    disabled: props.disabled,
  }
})

const linkProps = computed(() => {
  const baseProps: Record<string, any> = {
    as: 'a',
  }

  if (props.href) {
    baseProps.href = props.href
  }

  if (props.target) {
    baseProps.target = props.target
  }

  return baseProps
})
</script>

<template>
  <Button v-bind="{ ...mapped, ...linkProps }">
    <slot>{{ text }}</slot>
  </Button>
</template>
